/**
 * Shared formatting + rules for the member profile (admin directory, admin detail, /profile,
 * HR my-profile, the profile-completion banner).
 *
 * Dates of birth: the API returns DateTime values with a timezone offset
 * ("1990-05-12T00:00:00+02:00"). A birthday is a calendar date, so it is ALWAYS handled as the
 * date part of that string ("1990-05-12"). Never pass it through `new Date(iso)`, which shifts it
 * to the browser's zone and can move it a day.
 */
import dayjs from 'dayjs';
import type { Member, MemberModelView } from '@actschurch/ccms-sdk';
import { ccmsWallClock } from '@/utils/dateTime';

/** A member as the profile works with it: the Member row plus the enriched view fields when known. */
export type MemberRecord = Member & Partial<Omit<MemberModelView, keyof Member>>;

export const GENDER_OPTIONS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

/** "1990-05-12" from whatever the API sent, or null. */
export function dobDateOnly(value?: string | null): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

/** A local Date at midnight for a date picker. */
export function dobToDate(value?: string | null): Date | null {
  const d = dobDateOnly(value);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day);
}

/** Date picker value back to the "YYYY-MM-DD" the API accepts. */
export function dateToDob(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') return dobDateOnly(value);
  return dayjs(value).format('YYYY-MM-DD');
}

export function formatDob(value?: string | null): string {
  const d = dobDateOnly(value);
  return d ? dayjs(d).format('D MMMM YYYY') : 'Not set';
}

export function ageFromDob(value?: string | null): number | null {
  const d = dobDateOnly(value);
  if (!d) return null;
  const age = dayjs().diff(dayjs(d), 'year');
  return Number.isFinite(age) && age >= 0 ? age : null;
}

/** Mirrors MemberModelView.AgeGroup on the backend. */
export function ageGroupFor(age: number | null | undefined): { label: string; color: string } | null {
  if (age == null) return null;
  if (age < 13) return { label: 'Child', color: '#FF6B00' };
  if (age < 20) return { label: 'Teen', color: '#9C27B0' };
  if (age < 35) return { label: 'Young Adult', color: '#2196F3' };
  if (age < 60) return { label: 'Adult', color: '#00C853' };
  return { label: 'Senior', color: '#FFB800' };
}

/** Mirrors MemberModelView.MembershipStatus, worded for people. */
export function membershipStatusFor(m: Pick<Member, 'isMember' | 'isActive'>): { label: string; color: string } {
  if (!m.isMember) return { label: 'Visitor', color: 'gray' };
  return m.isActive ? { label: 'Active member', color: 'green' } : { label: 'Inactive member', color: 'yellow' };
}

/** yyyyMMddHHmmss (or ISO) stamp as "12 Oct 2026". */
export function formatStamp(value?: number | string | null): string {
  if (value === null || value === undefined || value === '' || value === 0) return 'Not recorded';
  const d = ccmsWallClock(value);
  return d.isValid() ? d.format('D MMM YYYY') : 'Not recorded';
}

/** yyyyMMddHHmmss (or ISO) stamp as "12 Oct 2026, 14:05". */
export function formatStampTime(value?: number | string | null): string {
  if (value === null || value === undefined || value === '' || value === 0) return 'Not recorded';
  const d = ccmsWallClock(value);
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : 'Not recorded';
}

export function memberFullName(m?: { firstName?: string | null; middleName?: string | null; lastName?: string | null } | null): string {
  if (!m) return '';
  return [m.firstName, m.middleName, m.lastName].map((p) => (p ?? '').trim()).filter(Boolean).join(' ');
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/** Same fields and labels as MemberProfileRules on the backend (GET /me/profile-status). */
export const REQUIRED_PROFILE_FIELDS: { field: string; label: string; isMissing: (m: Member) => boolean }[] = [
  { field: 'LastName', label: 'Surname', isMissing: (m) => !(m.lastName ?? '').trim() },
  { field: 'DateOfBirth', label: 'Date of birth', isMissing: (m) => !dobDateOnly(m.dateOfBirth) },
  { field: 'Gender', label: 'Gender', isMissing: (m) => !(m.gender ?? '').trim() },
  { field: 'MaritalStatusID', label: 'Marital status', isMissing: (m) => !(m.maritalStatusID > 0) },
  { field: 'CellNumber', label: 'Cellphone number', isMissing: (m) => !(m.cellNumber ?? '').trim() },
];

/** Friendly label for a server field name such as "MaritalStatusID". */
export function profileFieldLabel(field: string): string {
  return REQUIRED_PROFILE_FIELDS.find((f) => f.field.toLowerCase() === field.toLowerCase())?.label
    ?? field.replace(/ID$/, '').replace(/([a-z])([A-Z])/g, '$1 $2');
}

/** Labels for audit field names. */
const FIELD_LABELS: Record<string, string> = {
  firstname: 'First name',
  middlename: 'Middle name',
  lastname: 'Surname',
  dateofbirth: 'Date of birth',
  gender: 'Gender',
  maritalstatusid: 'Marital status',
  email: 'Email',
  cellnumber: 'Cellphone number',
  altcontact: 'Alternative contact',
  telephone: 'Telephone',
  ismember: 'Member',
  isactive: 'Active',
  avatar: 'Photo',
  typeid: 'Member type',
  membercode: 'Member code',
  hidebirthdayfromcolleagues: 'Hide birthday',
  planningcentrepersonid: 'Planning Centre link',
  anatomyuserid: 'Anatomy link',
  userid: 'Login',
  isdeleted: 'Deleted',
  mergedintomemberid: 'Merged into',
  mergedfrom: 'Merged from',
};

export function auditFieldLabel(field: string): string {
  return FIELD_LABELS[field.toLowerCase()] ?? field.replace(/([a-z])([A-Z])/g, '$1 $2');
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin edit', color: 'blue' },
  self: { label: 'Self service', color: 'teal' },
  hr: { label: 'HR', color: 'grape' },
  forms: { label: 'Forms', color: 'cyan' },
  status: { label: 'Status', color: 'orange' },
  'link-user': { label: 'Login link', color: 'indigo' },
  'planning-centre': { label: 'Planning Centre', color: 'violet' },
  delete: { label: 'Deleted', color: 'red' },
  restore: { label: 'Restored', color: 'green' },
  merge: { label: 'Merge', color: 'pink' },
  callcentre: { label: 'Call centre', color: 'teal' },
};

export function auditSource(source: string): { label: string; color: string } {
  return SOURCE_LABELS[source?.toLowerCase()] ?? { label: source || 'Change', color: 'gray' };
}

/** Error text for toasts and inline alerts. */
export function errorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === 'string' && e) return e;
  const maybe = e as { message?: string } | null;
  return maybe?.message || fallback;
}

export function errorStatus(e: unknown): number | undefined {
  const maybe = e as { status?: number; response?: { status?: number } } | null;
  return maybe?.status ?? maybe?.response?.status;
}

/** Trimmed text for a partial update: blank becomes null (which clears the field). */
export function textOrNull(value: string | null | undefined): string | null {
  const t = (value ?? '').trim();
  return t ? t : null;
}

/** Location select value ("0" is Online Campus, a real campus). */
export function locationValue(id: number | null | undefined): string | null {
  return id === null || id === undefined ? null : String(id);
}

export function parseLocationValue(value: string | null): number | null {
  if (value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
